import { useCallback, useEffect, useReducer, useRef } from "react";
import moment from "moment";
import {
  LifecycleService,
  fingerprintQuote,
} from "../../../services/Integrations/LifecycleService.ts";
import type {
  CanonicalQuote,
  CanonicalVehicle,
  Offer,
  ProviderId,
  QuoteResponse,
} from "../../../types/lifecycle.ts";
import {
  PARTY_KEYS,
  buildQuote,
  inferCustomerType,
  isDefaultProviderEnabled,
  resolveDraft,
  sameStreetAddress,
  seedParty,
  type DirtyFields,
  type Draft,
  type Party,
  type PartyField,
  type PartyKey,
  type SearchInputs,
} from "./model.ts";

interface CardBuffer<T> {
  base: T;
  values: T;
  copied?: boolean;
  edited?: Partial<Record<keyof T, true>>;
}

// A replaced draft invalidates its old buffer. Returning only changed buffers
// keeps the cards and the issue/requote guards in agreement.
const pendingBuffer = <T extends object>(
  base: T | undefined,
  buffer: CardBuffer<T> | null | undefined
): CardBuffer<T> | null =>
  base &&
  buffer?.base === base &&
  (buffer.copied ||
    (Object.keys(buffer.values) as (keyof T)[]).some(
      (key) => buffer.values[key] !== base[key]
    ))
    ? buffer
    : null;

interface QuoteState {
  inputs: SearchInputs;
  draft: Draft | null;
  dirty: DirtyFields;
  manual: DirtyFields;
  manualInputs: Partial<Record<keyof SearchInputs, true>>;
  resolvedFor: string;
  quote: {
    handle: string;
    offers: Offer[];
    priced: CanonicalQuote;
    streets: Record<PartyKey, string>;
    fingerprint: string;
  } | null;
  leasing: NonNullable<QuoteResponse["resolved"]>["leasing"] | null;
  resolvedRoles: NonNullable<QuoteResponse["holder"]>["roles"];
  provider: ProviderId | "";
  partyBuffers: Partial<Record<PartyKey, CardBuffer<Party>>>;
  vehicleBuffer: CardBuffer<CanonicalVehicle> | null;
}
const initial = (): QuoteState => ({
  inputs: {
    plate: "",
    certNumber: "",
    pin: "",
    purpose: "",
    vehicleType: "",
    installments: 1,
    coverageStart: moment().add(1, "day").format("YYYY-MM-DD"),
  },
  draft: null,
  dirty: {},
  manual: {},
  manualInputs: {},
  resolvedFor: "",
  quote: null,
  leasing: null,
  resolvedRoles: {},
  provider: "",
  partyBuffers: {},
  vehicleBuffer: null,
});
type Action =
  | { type: "patch"; patch: Partial<QuoteState> }
  | {
      type: "input";
      key: keyof SearchInputs;
      value: SearchInputs[keyof SearchInputs];
    }
  | { type: "party"; party: PartyKey; key: PartyField; value: string }
  | { type: "vehicle"; key: keyof CanonicalVehicle; value: string | number }
  | { type: "copy"; from: PartyKey; to: PartyKey }
  | {
      type: "commitParty" | "bufferParty";
      party: PartyKey;
      values: Partial<Party>;
      copied?: boolean;
    }
  | { type: "commitVehicle"; values: Partial<CanonicalVehicle> }
  | {
      type: "bufferVehicle";
      key: keyof CanonicalVehicle;
      value: string | number;
    }
  | { type: "discardParty"; party: PartyKey }
  | { type: "discardVehicle" }
  | {
      type: "hydrateParty";
      party: PartyKey;
      pin: string;
      values: Partial<Party>;
    }
  | {
      type: "hydrateVehicle";
      plate: string;
      values: Partial<CanonicalVehicle>;
      inputs: Partial<SearchInputs>;
    };

const changedValues = <T extends object>(
  base: T,
  patch: Partial<T>
): Partial<T> =>
  Object.fromEntries(
    Object.entries(patch).filter(([key, value]) => base[key] !== value)
  ) as Partial<T>;
const fieldFlags = (group: PartyKey | "vehicle", values: object): DirtyFields =>
  Object.fromEntries(
    Object.keys(values).map((key) => [`${group}.${key}`, true])
  );
const withoutManual = <T extends object>(
  values: Partial<T>,
  manual: DirtyFields,
  group: PartyKey | "vehicle"
): Partial<T> =>
  Object.fromEntries(
    Object.entries(values).filter(([key]) => !manual[`${group}.${key}`])
  ) as Partial<T>;
const mergeBuffer = <T extends object>(
  buffer: CardBuffer<T>,
  base: T,
  values: Partial<T>
): CardBuffer<T> => ({
  ...buffer,
  base,
  values: {
    ...buffer.values,
    ...Object.fromEntries(
      Object.entries(values).filter(([key]) => !buffer.edited?.[key])
    ),
  },
});
const quoteFingerprint = (
  inputs: SearchInputs,
  draft: Draft | null,
  dirty: DirtyFields,
  streets: Record<PartyKey, string>
) => {
  const compared = { ...dirty };
  // Compare addresses with the quote's resolved data, including when no person
  // was known before the first request. Cosmetic merges keep the saved wording
  // without invalidating the price or modifying the original insurer payload.
  if (draft)
    for (const party of PARTY_KEYS)
      if (sameStreetAddress(draft[party].street, streets[party]))
        delete compared[`${party}.street`];
  return fingerprintQuote(buildQuote(inputs, draft, compared));
};
const editParty = (
  state: QuoteState,
  party: PartyKey,
  key: PartyField,
  value: string
): QuoteState => {
  if (!state.draft) return state;
  const identityChanged =
    key === "pin" && value.trim() !== state.draft[party].pin.trim();
  const updated: Party = {
    ...(identityChanged ? seedParty() : state.draft[party]),
    [key]: value,
  };
  if (!["settlementCode", "street", "phone"].includes(key)) {
    if (!state.dirty[`${party}.settlementCode`]) updated.settlementCode = "";
    if (!state.dirty[`${party}.street`]) updated.street = "";
  }
  if (key === "pin" && !state.dirty[`${party}.customerType`])
    updated.customerType =
      party === "driver"
        ? "natural"
        : inferCustomerType(value) ?? updated.customerType;
  return {
    ...state,
    draft: { ...state.draft, [party]: updated },
    dirty: {
      ...Object.fromEntries(
        Object.entries(state.dirty).filter(
          ([field]) => !identityChanged || !field.startsWith(`${party}.`)
        )
      ),
      [`${party}.${key}`]: true,
    },
    manual: {
      ...Object.fromEntries(
        Object.entries(state.manual).filter(
          ([field]) => !identityChanged || !field.startsWith(`${party}.`)
        )
      ),
      [`${party}.${key}`]: true,
    },
    inputs:
      party === "holder" && key === "pin"
        ? { ...state.inputs, pin: value }
        : state.inputs,
  };
};
const replaceParty = (
  state: QuoteState,
  key: PartyKey,
  party: Party
): QuoteState => {
  if (!state.draft || (key === "driver" && party.customerType === "legal"))
    return state;
  return {
    ...state,
    draft: { ...state.draft, [key]: { ...party } },
    dirty: {
      ...state.dirty,
      ...Object.fromEntries(
        Object.keys(party).map((field) => [`${key}.${field}`, true])
      ),
    },
    manual: { ...state.manual, ...fieldFlags(key, party) },
    inputs:
      key === "holder" ? { ...state.inputs, pin: party.pin } : state.inputs,
  };
};
const reducer = (state: QuoteState, action: Action): QuoteState => {
  switch (action.type) {
    case "patch":
      return { ...state, ...action.patch };
    case "input": {
      if (action.key === "plate" || action.key === "certNumber")
        return {
          ...initial(),
          inputs: {
            ...state.inputs,
            [action.key]: String(action.value).toUpperCase(),
            pin: "",
            purpose: "",
            vehicleType: "",
          },
        };
      if (action.key === "pin" && state.draft)
        return editParty(state, "holder", "pin", String(action.value));
      return {
        ...state,
        inputs: { ...state.inputs, [action.key]: action.value },
        manualInputs: { ...state.manualInputs, [action.key]: true },
      };
    }
    case "party":
      return editParty(state, action.party, action.key, action.value);
    case "vehicle":
      return state.draft
        ? {
            ...state,
            draft: {
              ...state.draft,
              vehicle: { ...state.draft.vehicle, [action.key]: action.value },
            },
            dirty: { ...state.dirty, [`vehicle.${action.key}`]: true },
            manual: { ...state.manual, [`vehicle.${action.key}`]: true },
          }
        : state;
    case "copy":
      return state.draft
        ? replaceParty(state, action.to, state.draft[action.from])
        : state;
    case "bufferParty": {
      const base = state.draft?.[action.party];
      if (!base) return state;
      const current = state.partyBuffers[action.party];
      const buffer = current?.base === base ? current : null;
      const identityChanged =
        action.values.pin !== undefined &&
        action.values.pin.trim() !== (buffer?.values ?? base).pin.trim();
      const values = {
        ...(identityChanged
          ? seedParty({
              customerType:
                action.party === "driver"
                  ? "natural"
                  : inferCustomerType(action.values.pin) ?? "natural",
            })
          : buffer?.values ?? base),
        ...action.values,
      };
      return {
        ...state,
        partyBuffers: {
          ...state.partyBuffers,
          [action.party]: {
            base,
            values,
            copied: action.copied || (!identityChanged && buffer?.copied),
            edited: {
              ...(!identityChanged ? buffer?.edited : {}),
              ...Object.fromEntries(
                Object.keys(action.values).map((key) => [key, true])
              ),
            },
          },
        },
      };
    }
    case "bufferVehicle": {
      const base = state.draft?.vehicle;
      if (!base) return state;
      const buffer = state.vehicleBuffer;
      return {
        ...state,
        vehicleBuffer: {
          base,
          values: {
            ...(buffer?.base === base ? buffer.values : base),
            [action.key]: action.value,
          },
          edited: {
            ...(buffer?.base === base ? buffer.edited : {}),
            [action.key]: true,
          },
        },
      };
    }
    case "discardParty":
      return {
        ...state,
        partyBuffers: { ...state.partyBuffers, [action.party]: undefined },
      };
    case "discardVehicle":
      return { ...state, vehicleBuffer: null };
    case "commitParty": {
      const before = state.draft?.[action.party];
      if (!before) return state;
      // Copy is a complete replacement, including equal or empty fields. Running
      // identity edits individually would clear copied addresses and infer types.
      if (action.copied)
        return replaceParty(state, action.party, {
          ...before,
          ...action.values,
        });
      const pending = state.partyBuffers[action.party];
      const buffer = pending?.base === before ? pending : null;
      if (buffer) {
        const updated = { ...before, ...action.values };
        const changed = changedValues(before, updated);
        return {
          ...state,
          draft: { ...state.draft, [action.party]: updated },
          inputs:
            action.party === "holder" && before.pin !== updated.pin
              ? { ...state.inputs, pin: updated.pin }
              : state.inputs,
          dirty: { ...state.dirty, ...fieldFlags(action.party, changed) },
          manual: {
            ...Object.fromEntries(
              Object.entries(state.manual).filter(
                ([key]) =>
                  before.pin.trim() === updated.pin.trim() ||
                  !key.startsWith(`${action.party}.`)
              )
            ),
            ...fieldFlags(action.party, buffer.edited ?? changed),
          },
          partyBuffers: { ...state.partyBuffers, [action.party]: undefined },
        };
      }
      // Apply only what actually differs, field by field through editParty, so the
      // per-field side effects (address reset, customer-type inference, holder pin
      // sync) are exactly those of editing each field directly.
      return (Object.keys(action.values) as PartyField[])
        .filter(
          (key) =>
            action.values[key] !== undefined &&
            action.values[key] !== before[key]
        )
        .reduce(
          (next, key) =>
            editParty(next, action.party, key, action.values[key] as string),
          state
        );
    }
    case "commitVehicle": {
      if (!state.draft) return state;
      const vehicle = state.draft.vehicle;
      const buffer =
        state.vehicleBuffer?.base === vehicle ? state.vehicleBuffer : null;
      const changed = Object.entries(action.values).filter(
        ([key, value]) => vehicle[key as keyof CanonicalVehicle] !== value
      );
      if (!changed.length && !buffer) return state;
      return {
        ...state,
        draft: {
          ...state.draft,
          vehicle: { ...vehicle, ...Object.fromEntries(changed) },
        },
        dirty: {
          ...state.dirty,
          ...Object.fromEntries(
            changed.map(([key]) => [`vehicle.${key}`, true])
          ),
        },
        manual: {
          ...state.manual,
          ...fieldFlags(
            "vehicle",
            buffer?.edited ?? Object.fromEntries(changed)
          ),
        },
        vehicleBuffer: null,
      };
    }
    case "hydrateParty": {
      if (!state.draft) return state;
      const base = state.draft[action.party];
      const buffer = pendingBuffer(base, state.partyBuffers[action.party]);
      const view = buffer?.values ?? base;
      if (view.pin.trim() !== action.pin) return state;
      // A newly typed identifier belongs to a new person; never merge it into
      // the committed person or carry the old person's manually edited fields.
      const samePerson = base.pin.trim() === action.pin;
      const values = withoutManual(
        action.values,
        samePerson ? state.manual : {},
        action.party
      );
      const patch = samePerson ? changedValues(base, values) : {};
      const updated = Object.keys(patch).length ? { ...base, ...patch } : base;
      const merged = buffer ? mergeBuffer(buffer, updated, values) : null;
      if (
        updated === base &&
        (!buffer ||
          !Object.keys(changedValues(buffer.values, merged.values)).length)
      )
        return state;
      return {
        ...state,
        draft:
          updated === base
            ? state.draft
            : { ...state.draft, [action.party]: updated },
        dirty: { ...state.dirty, ...fieldFlags(action.party, patch) },
        partyBuffers: {
          ...state.partyBuffers,
          [action.party]: merged ?? undefined,
        },
      };
    }
    case "hydrateVehicle": {
      if (
        !state.draft ||
        state.inputs.plate.trim().toUpperCase() !== action.plate
      )
        return state;
      const base = state.draft.vehicle;
      const values = withoutManual(action.values, state.manual, "vehicle");
      const patch = changedValues(base, values);
      const updated = Object.keys(patch).length ? { ...base, ...patch } : base;
      const buffer = pendingBuffer(base, state.vehicleBuffer);
      const merged = buffer ? mergeBuffer(buffer, updated, values) : null;
      const inputPatch = changedValues(
        state.inputs,
        Object.fromEntries(
          Object.entries(action.inputs).filter(
            ([key]) => !state.manualInputs[key]
          )
        )
      );
      if (
        updated === base &&
        !Object.keys(inputPatch).length &&
        (!buffer ||
          !Object.keys(changedValues(buffer.values, merged.values)).length)
      )
        return state;
      return {
        ...state,
        draft: { ...state.draft, vehicle: updated },
        inputs: { ...state.inputs, ...inputPatch },
        dirty: { ...state.dirty, ...fieldFlags("vehicle", patch) },
        vehicleBuffer: merged,
      };
    }
  }
};
export function useLifecycleQuote(jwt: string) {
  const [state, dispatch] = useReducer(reducer, undefined, initial);
  const partyEdits = Object.fromEntries(
    PARTY_KEYS.map((party) => [
      party,
      pendingBuffer(state.draft?.[party], state.partyBuffers[party]),
    ])
  ) as Record<PartyKey, CardBuffer<Party> | null>;
  const vehicleEdits = pendingBuffer(state.draft?.vehicle, state.vehicleBuffer);
  const hasPendingEdits = Boolean(
    vehicleEdits || PARTY_KEYS.some((party) => partyEdits[party])
  );
  const version = useRef(0);
  const loading = useRef(false);
  const latest = useRef(state);
  latest.current = state;
  const edit = (action: Action) => {
    const next = reducer(latest.current, action);
    if (next === latest.current) return;
    latest.current = next;
    version.current++;
    dispatch(action);
  };
  useEffect(
    () => () => {
      version.current++;
    },
    [jwt]
  );
  const search = useCallback(
    async (prepare?: (quote: CanonicalQuote) => Promise<CanonicalQuote>) => {
      if (
        loading.current ||
        hasPendingEdits ||
        !state.inputs.plate.trim() ||
        !state.inputs.certNumber.trim()
      )
        return;
      loading.current = true;
      const currentVersion = version.current;
      try {
        const key = `${state.inputs.plate
          .trim()
          .toUpperCase()}|${state.inputs.certNumber.trim()}`;
        const sameVehicle = key === state.resolvedFor;
        let dirty = sameVehicle ? state.dirty : {};
        const carried = sameVehicle ? state.draft : null;
        const original = buildQuote(state.inputs, carried, dirty);
        const requested = prepare ? await prepare(original) : original;
        if (version.current !== currentVersion) return;
        const response = await LifecycleService.getQuote(jwt, requested);
        if (version.current !== currentVersion) return;
        const draft = resolveDraft(response, carried, dirty);
        if (prepare) {
          // These values were included in the priced request. Keep them above the
          // returned insurer data and carry the same overrides into recalculation.
          for (const party of PARTY_KEYS) {
            if (!requested[party]) continue;
            draft[party] = { ...draft[party], ...requested[party] };
            dirty = { ...dirty, ...fieldFlags(party, requested[party]) };
          }
          if (requested.vehicle) {
            draft.vehicle = { ...draft.vehicle, ...requested.vehicle };
            const {
              vehicleType: _type,
              providerMappings: _mappings,
              ...fields
            } = requested.vehicle;
            dirty = { ...dirty, ...fieldFlags("vehicle", fields) };
          }
        }
        const inputs: SearchInputs = {
          ...state.inputs,
          purpose: requested.purpose || response.resolved?.purpose || "",
          vehicleType:
            requested.vehicle?.vehicleType ||
            response.vehicle?.vehicleType ||
            response.resolved?.vehicleType ||
            "",
        };
        const streets = Object.fromEntries(
          PARTY_KEYS.map((party) => [party, draft[party].street])
        ) as Record<PartyKey, string>;
        const best = response.offers
          .filter(
            (offer) =>
              isDefaultProviderEnabled(offer.provider) &&
              offer.available &&
              offer.canIssue &&
              offer.premium
          )
          .sort(
            (a, b) =>
              (a.premium?.eur ?? Infinity) - (b.premium?.eur ?? Infinity)
          )[0];
        dispatch({
          type: "patch",
          patch: {
            inputs,
            draft,
            dirty,
            resolvedFor: key,
            provider: best?.provider ?? "",
            leasing: response.resolved?.leasing ?? null,
            resolvedRoles: response.holder?.roles ?? {},
            quote: {
              handle: response.quoteHandle,
              offers: response.offers,
              priced: response.quote,
              streets,
              fingerprint: quoteFingerprint(inputs, draft, dirty, streets),
            },
          },
        });
      } finally {
        loading.current = false;
      }
    },
    [jwt, state, hasPendingEdits]
  );
  return {
    ...state,
    partyEdits,
    get searching() {
      return loading.current;
    },
    vehicleEdits,
    hasPendingEdits,
    stale: Boolean(
      state.quote &&
        state.quote.fingerprint !==
          quoteFingerprint(
            state.inputs,
            state.draft,
            state.dirty,
            state.quote.streets
          )
    ),
    search,
    setInput: <K extends keyof SearchInputs>(key: K, value: SearchInputs[K]) =>
      edit({ type: "input", key, value }),
    setVeh: (key: keyof CanonicalVehicle, value: string | number) =>
      edit({ type: "vehicle", key, value }),
    setParty: (party: PartyKey, key: PartyField, value: string) =>
      edit({ type: "party", party, key, value }),
    copyParty: (from: PartyKey, to: PartyKey) =>
      edit({ type: "copy", from, to }),
    bufferParty: (party: PartyKey, values: Partial<Party>, copied = false) =>
      edit({ type: "bufferParty", party, values, copied }),
    bufferVehicle: (key: keyof CanonicalVehicle, value: string | number) =>
      edit({ type: "bufferVehicle", key, value }),
    discardParty: (party: PartyKey) => edit({ type: "discardParty", party }),
    discardVehicle: () => edit({ type: "discardVehicle" }),
    /** Write a card's buffered edits into the draft in one dispatch. */
    commitParty: (party: PartyKey, values: Partial<Party>, copied = false) =>
      edit({ type: "commitParty", party, values, copied }),
    commitVehicle: (values: Partial<CanonicalVehicle>) =>
      edit({ type: "commitVehicle", values }),
    hydrateParty: (party: PartyKey, pin: string, values: Partial<Party>) =>
      edit({ type: "hydrateParty", party, pin, values }),
    hydrateVehicle: (
      plate: string,
      values: Partial<CanonicalVehicle>,
      inputs: Partial<SearchInputs>
    ) => edit({ type: "hydrateVehicle", plate, values, inputs }),
    setProvider: (provider: ProviderId | "") =>
      dispatch({ type: "patch", patch: { provider } }),
    clearQuote: () => dispatch({ type: "patch", patch: { quote: null } }),
  };
}
